import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoModule } from '@ngneat/transloco';
import {
  findHdChartPointDisplay,
  HdChartPointKey,
  HdPlanetCycleInfo,
  getHdPlanetCycleUnitShort,
} from '../planet-grid/hd-planet-display-config';
import { HdPlanetInfoService } from '../hd-planet-info.service';
import { HdContentLanguageService } from '../hd-content-language.service';

/**
 * Cheat-sheet kaart voor één planeet — analoog aan
 * `HdGateInfoCheatSheetComponent` maar dan voor de algemene planeet-info
 * (cheat-sheet 1 in `hd-planets-info.md`).
 *
 * Toont:
 *  - top-stripe in de planet-stripe-kleur;
 *  - astronomicon glyph + naam links/midden in de header;
 *  - cyclus-info rechts in de header (`<perCycle> d/c · <daysPerGate> d/p · <daysPerLine> d/l`);
 *  - subtitel + 5 bullets;
 *  - optionele footer-tagline.
 *
 * Reactief op taalwissels via `HdPlanetInfoService.planetTranslations`.
 */
@Component({
  selector: 'hd-planet-card',
  templateUrl: './hd-planet-card.component.html',
  styleUrl: './hd-planet-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule],
})
export class HdPlanetCardComponent {
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Planeet of astrologisch punt (AC, DC, MC, IC, Lilith; spec astro posities 2026-10-06). */
  readonly planetKey = input.required<HdChartPointKey>();

  /** Verbergt de cyclus-getallen (header rechts). */
  readonly hideCycleInfo = input(false);
  /** Verbergt de bullets-lijst. */
  readonly hideBullets = input(false);
  /** Verbergt de subtitel. */
  readonly hideSubtitle = input(false);
  /** Compacte variant (kleinere padding/font). */
  readonly compact = input(false);

  protected readonly config = computed(() => {
    const key = this.planetKey();
    const display = findHdChartPointDisplay(key);
    if (!display) {
      throw new Error(`Unknown planet key: ${key}`);
    }
    return display;
  });

  protected readonly translations = computed(() =>
    this.planetInfoService.planetInfoIn(this.planetKey(), this.contentLang.effectiveLang()),
  );

  /** Leeg voor punten zonder zinvolle cyclus (de hoeken draaien dagelijks rond). */
  protected readonly cycleSegments = computed(() => {
    const info = this.config().cycleInfo;
    return info ? formatCycleSegments(info) : [];
  });
}

interface CycleSegment {
  readonly value: string;
  readonly unitKey: 'd/c' | 'd/p' | 'd/l' | 'j/c' | null;
  /** Wanneer gezet: vrije-tekst (extra periode), `value`/`unitKey` worden genegeerd. */
  readonly extraText?: string;
}

function formatCycleSegments(info: HdPlanetCycleInfo): readonly CycleSegment[] {
  const segments: CycleSegment[] = [
    { value: formatNumber(info.perCycle), unitKey: getHdPlanetCycleUnitShort(info.cycleUnit) },
    { value: formatNumber(info.daysPerGate), unitKey: 'd/p' },
    { value: formatNumber(info.daysPerLine), unitKey: 'd/l' },
  ];
  if (info.extraDescription) {
    segments.push({ value: '', unitKey: null, extraText: info.extraDescription });
  }
  return segments;
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
}
